import { getUrlParams } from 'widgets/toolbox/util';
import type { TWidget } from 'widgets/Widget';

import { TBillingFormInstance } from 'widgets/checkout/BillingForm';
import { TLabelInstance } from 'widgets/global/Label';
import { TProcessButtonInstance } from 'widgets/global/ProcessButton';
import { TOrderModel } from 'types/models/order';

/**
 * @description Checkout Billing step logic
 *
 * @param CheckoutMgr Base widget for extending
 */
function BillingStepMixinClassCreator(CheckoutMgr: import('widgets/checkout/CheckoutMgr').TCheckoutMgr) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class BillingStepMixin
     * @augments CheckoutMgr
     * @classdesc Represents billing step component with the next features:
     * 1. Manage Billing step state on the checkout
     * 2. Activate/Deactivate billing step
     * 3. Submit billing step
     */
    class BillingStepMixin extends CheckoutMgr {
        submitButtons: Array<TProcessButtonInstance | {skipPrivacyValidation: boolean}> = [];

        skipPrivacyValidation?: boolean;

        /**
         * @description Activate billing step
         */
        billingActivate() {
            this.getById(
                this.prefs().billingFormID,
                (billingFormComponent: TBillingFormInstance) => {
                    billingFormComponent.activate({
                        order: this.order,
                        customer: this.customer
                    });
                    const urlParams = <{paymentError: string}> getUrlParams();

                    if (urlParams.paymentError) {
                        billingFormComponent.setError(urlParams.paymentError.toString());
                    }
                }
            );
            this.eventBus().emit('checkout.billing.step.activated', this);
        }

        /**
         * @description Deactivate billing step
         */
        billingDeActivate() {
            // Placeholder for additional logic on billing deactivation
        }

        /**
         * @description Submit billing form
         * @param submitButtons Event target
         */
        billingSubmit(...submitButtons) {
            this.submitButtons = submitButtons.flat();
            this.submitButtons.forEach(target => {
                if ('skipPrivacyValidation' in target) {
                    this.skipPrivacyValidation = target.skipPrivacyValidation;
                }
            });

            if (this.prefs().skipSummaryStep && !this.callIfExists('validatePlaceOrderForm', this.skipPrivacyValidation)) { return; }

            this.getById(this.prefs().billingFormID, (billingFormComponent: TBillingFormInstance) => {
                billingFormComponent.submit();
            });
        }

        /**
         *
         * @description Billing submission result handler
         * @param billingForm - Billing form widget
         * @param data - Submission response from server
         * @listens BillingForm#submit
         */
        billingSubmitted(billingForm: TBillingFormInstance, data: Record<string, unknown>) {
            if (data.order) {
                this.order = <TOrderModel>data.order;
            }

            if (this.prefs().skipSummaryStep) {
                this.callIfExists('updateSummary', data?.order, true);
                this.callIfExists('summarySubmit', this.submitButtons, this.skipPrivacyValidation);
            } else {
                this.callIfExists('updateSummaryAndNext', billingForm, data);
            }
        }

        /**
         * @description Handle next payment button toggling
         * @param childWidget - Child widget, that fired event
         * @param display - Display or hide button
         * @param displayPrivacyCheckbox - Determines whether should be hidden privacy checkbox or not(e.g. skipping order review step enabled, PayPal method selected)
         * @listens "togglepaymentnextbtn"
         */
        toggleNextPaymentButton(childWidget: InstanceType<TWidget>, display: boolean, displayPrivacyCheckbox = true) {
            this.getById(
                'paymentNext',
                (paymentNext: TLabelInstance) => paymentNext.toggle(display)
            );

            if (!this.prefs().skipSummaryStep) {
                return;
            }

            this.ref('agreeToPrivacyCheckbox').toggle(displayPrivacyCheckbox);
        }

        /**
         * @description Handle next payment button active state
         * @param state - Display or hide button
         * @listens "handlenextpaymentbuttonstate"
         */
        handleNextPaymentButtonState(state: boolean) {
            this.getById(
                'paymentNext',
                (paymentNext: TLabelInstance) => {
                    if (paymentNext.isShown()) {
                        if (state) {
                            paymentNext.ref('submitButton').enable();
                        } else {
                            paymentNext.ref('submitButton').disable();
                        }
                    }
                }
            );
        }
    }

    return BillingStepMixin;
}

export type TBillingStepMixin = ReturnType<typeof BillingStepMixinClassCreator>;

export type TBillingStepMixinInstance = InstanceType<TBillingStepMixin>;

export default BillingStepMixinClassCreator;
